import { useContext, useEffect, useMemo, useState } from "react";
import apiService from "../../../services/service";
import API_ROUTES from "../../../api-routes";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { z } from "zod";
import { EntityContext } from "..";

function useSetUpProcessModel() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { entity } = useContext(EntityContext);
  const { role } = useSelector((state) => state.login);
  const [serviceLine, setServiceLine] = useState(null);
  const [processType, setProcessType] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [processesTypesData, setProcessesTypesData] = useState([]);
  const [isProcessFetch, setIsProcessFetch] = useState(false);
  const [toggleProcessModal, setToggleProcessModal] = useState(false);
  const [showProcessExistModal, setShowProcessExistModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [utcDate, setUtcDate] = useState(null);

  const createProcess = async (processData, force) => {
    try {
      setLoading(true);
      const { data } = await apiService(API_ROUTES.ADD_PROCESS, "POST", {
        replace: { entity_id: id },
        body: {
          template: processData.template,
          startDate: processData.startDate,
          force,
        },
        schema: z.object({
          entity_id: z.string().uuid(),
          template: z.string().uuid(),
          startDate: z.string().datetime(),
          force: z.boolean().optional(),
        }),
      });
      if (data.processExists) {
        setToggleProcessModal(false);
        setShowProcessExistModal(true);
      }
      if (data.process) {
        const processId = data.process;
        setToggleProcessModal(false);
        setShowProcessExistModal(false);
        navigate(`/process/${processId}/milestones`);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleCreateProcess = ({ force = false }) => {
    if (!utcDate) {
      return;
    }
    const processData = {
      template: processType,
      startDate: utcDate,
    };
    createProcess(processData, force);
  };

  const formattedServices =
    entity?.active_services
      ?.map((service) => ({
        label: service.name,
        value: service.id,
      }))
      .filter((service) => service?.label !== "Payroll +") || [];

  const formattedProcessTypes = useMemo(() => {
    return (
      processesTypesData
        ?.filter((process) => process.service === serviceLine)
        .map((process) => ({
          label: process.name,
          value: process.id,
          processId: process.id,
        })) || []
    );
  }, [processesTypesData, serviceLine]);

  useEffect(() => {
    if (formattedServices.length === 1 && toggleProcessModal) {
      setServiceLine(formattedServices[0].value);
    }
  }, [formattedServices]);
  const handleSelectServiceLine = async (e) => {
    setServiceLine(e.value);
    setProcessType(null);
    setProcessType(e.value);
  };

  const handleOpenProcessModal = async () => {
    setIsProcessFetch(true);
    await fetchProcessTypes();
    setIsProcessFetch(false);
    setToggleProcessModal(true);
  };
  const fetchProcessTypes = async () => {
    const { data } = await apiService(`${API_ROUTES.FETCH_PROCESS_TYPE}`);
    setProcessesTypesData(data);
  };
  const handleCloseModal = () => {
    setToggleProcessModal(false);
    setServiceLine(null);
    setProcessType(null);
    setStartDate(null);
    setShowProcessExistModal(false);
  };
  const hasOnlyPayrollPlus = useMemo(() => {
    const services = entity?.active_services ?? [];
    if (services.length !== 1) return false;
    return services[0]?.name === "Payroll +";
  }, [entity]);

  return {
    serviceLine,
    setServiceLine,
    setProcessType,
    processType,
    startDate,
    setStartDate,
    processesTypesData,
    createProcess,
    handleCreateProcess,
    formattedServices,
    formattedProcessTypes,
    dispatch,
    handleSelectServiceLine,
    toggleProcessModal,
    setToggleProcessModal,
    handleOpenProcessModal,
    isProcessFetch,
    entity,
    handleCloseModal,
    role,
    loading,
    setUtcDate,
    showProcessExistModal,
    hasOnlyPayrollPlus,
  };
}

export default useSetUpProcessModel;
